import { getExperiment, normalizeParams } from '@physlab/core';
import { buildCollisionQuestions } from './questions/collision.js';
import { buildCircularQuestions } from './questions/circular.js';
import { buildInclineQuestions } from './questions/incline.js';
import { buildPendulumQuestions } from './questions/pendulum.js';
import { difficultyInfo, seededRng } from './questionUtils.js';
import { buildLabGuide } from './guide.js';
import { runConservationSelfTest, selfTestSummary } from './selfTest.js';

const BUILDERS = {
  pendulum: buildPendulumQuestions,
  collision: buildCollisionQuestions,
  incline: buildInclineQuestions,
  circular: buildCircularQuestions,
};

const MAX_COUNTS = { exerciseCount: 5, thinkingCount: 3 };

function clampCount(value, fallback, max) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(1, Math.round(number)));
}

/**
 * 教学 Agent：确定性规则引擎。
 * 不依赖外部大模型服务，所有数值均由当前物理参数和公式推导，便于课堂离线使用。
 */
export class TeachingAgent {
  constructor({ version = '1.0.0' } = {}) {
    this.version = version;
  }

  /**
   * @param {{
   *  experiment: string|object,
   *  params: object,
   *  difficulty?: 'basic'|'standard'|'challenge',
   *  exerciseCount?: number,
   *  thinkingCount?: number,
   *  seed?: number,
   *  generatedAt?: Date|string
   * }} options
   */
  generate(options) {
    const model = typeof options.experiment === 'string' ? getExperiment(options.experiment) : options.experiment;
    const builder = BUILDERS[model.id];
    if (!builder) throw new Error(`未注册的实验类型：${model.id}`);
    const difficulty = difficultyInfo(options.difficulty).key;
    const exerciseCount = clampCount(options.exerciseCount, 4, MAX_COUNTS.exerciseCount);
    const thinkingCount = clampCount(options.thinkingCount, 2, MAX_COUNTS.thinkingCount);
    const difficultyCap = difficulty === 'basic' ? 2 : difficulty === 'standard' ? 3 : 4;
    const seed = Number.isFinite(options.seed) ? options.seed : Date.now() % 2147483647;
    const rng = seededRng(seed);
    const { params } = normalizeParams(model, options.params);
    const generated = builder({
      params,
      rng,
      difficultyLevel: difficultyCap,
      exerciseCount,
      thinkingCount,
    });
    const generatedAt = options.generatedAt ? new Date(options.generatedAt) : new Date();
    const all = [...generated.exercises, ...generated.thinking];
    const summary = `已基于当前参数生成 ${generated.exercises.length} 道练习题、${generated.thinking.length} 道思考题，均附标准答案解析。`;
    return {
      experimentId: model.id,
      experimentTitle: model.meta.title,
      difficulty,
      difficultyLabel: difficultyInfo(difficulty).label,
      seed,
      generatedAt: generatedAt.toISOString(),
      params,
      exercises: generated.exercises,
      thinking: generated.thinking,
      all,
      counts: { exercises: generated.exercises.length, thinking: generated.thinking.length, total: all.length },
      summary,
      version: this.version,
    };
  }

  buildGuide({
    experiment,
    params,
    metrics,
    exerciseSet,
    includeAnswers = true,
    generatedAt = new Date(),
  }) {
    const model = typeof experiment === 'string' ? getExperiment(experiment) : experiment;
    return buildLabGuide({
      model,
      params,
      metrics,
      exerciseSet,
      includeAnswers,
      generatedAt,
      agentVersion: this.version,
    });
  }

  /**
   * 浏览器内自检。若存在失败项且提供了运行器，则触发稳定性修复。
   */
  runSelfTest({ runner = null } = {}) {
    const first = runConservationSelfTest();
    const failed = first.filter((item) => !item.passed);
    let repaired = false;
    if (failed.length > 0 && runner) {
      runner.stabilize();
      repaired = true;
    }
    return {
      results: first,
      summary: selfTestSummary(first),
      failed: failed.map((item) => item.name),
      repaired,
      at: new Date().toISOString(),
      version: this.version,
    };
  }

  /** 供 UI 在参数异常时调用的安全参数修复。 */
  sanitize(experiment, params) {
    const model = typeof experiment === 'string' ? getExperiment(experiment) : experiment;
    return normalizeParams(model, params);
  }
}

export const teachingAgent = new TeachingAgent();
export { buildLabGuide } from './guide.js';
export { runConservationSelfTest } from './selfTest.js';
export { DIFFICULTIES, difficultyInfo } from './questionUtils.js';
